/**
 * "Vouchers must be redeemed in person" notice for the public Voucher Redemption Locator (business
 * feedback 2026-10-22). Shown by VoucherLocator.tsx the first time per browser session that the
 * member runs a search (Search button, Enter in the ZIP field, or Use My Location) — alongside the
 * results, which load behind it. Never on page load, so typing is never interrupted.
 *
 * Accessible modal: role="dialog" + aria-modal, labelled and described by its own text; focus moves
 * to the "OK, got it" button on open, Tab/Shift+Tab stay inside, Esc / the button / a click on the
 * backdrop close it, and focus returns to whatever had it before (the Search button, the ZIP field…).
 * Rendered inside .vl-page so it inherits the page's brand fonts and colour tokens.
 */
import React, { useEffect, useRef } from "react";

import { IN_PERSON_NOTICE } from "../../utils/voucherLocator";

interface Props {
  onClose: () => void;
}

const InPersonNotice: React.FC<Props> = ({ onClose }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const okRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    okRef.current?.focus();
    return () => {
      // the trigger may be disabled while the search runs; focus() is then a no-op, which is fine
      previous?.focus?.();
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab") {
      return;
    }
    const focusables = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    ).filter((el) => !el.hasAttribute("disabled"));
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="vl-modal-backdrop"
      data-testid="voucher-locator-in-person-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="vl-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vl-in-person-title"
        aria-describedby="vl-in-person-body"
        data-testid="voucher-locator-in-person-modal"
        onKeyDown={onKeyDown}
      >
        <svg
          className="vl-modal-icon"
          viewBox="0 0 24 24"
          width="40"
          height="40"
          aria-hidden="true"
          focusable="false"
        >
          {/* storefront */}
          <path
            d="M4 10v10h16V10M3 4h18l1 5a3 3 0 0 1-5.5 1.6A3 3 0 0 1 12 12a3 3 0 0 1-4.5-1.4A3 3 0 0 1 2 9l1-5zM10 20v-5h4v5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
        <h2 id="vl-in-person-title">{IN_PERSON_NOTICE.title}</h2>
        <p id="vl-in-person-body">{IN_PERSON_NOTICE.body}</p>
        <button
          ref={okRef}
          type="button"
          className="vl-btn vl-btn-maroon vl-modal-ok"
          onClick={onClose}
        >
          {IN_PERSON_NOTICE.button}
        </button>
      </div>
    </div>
  );
};

export default InPersonNotice;
